import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { arrayUnion, writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import { todayISO } from '../domain/clock';
import {
  planAdd, planDelete, planEdit, planTopUp, planUpdateExisting,
} from '../domain/mileage';
import { useCollectionWindow } from './useCollectionWindow';
import { absentAtServer } from './serverConfirmed';

// The Mileage Log (#476): one `mileageEntries` document per trip.
//
// A trip is a drive somebody made, so a new one has a free id — two devices
// logging the same drive offline have logged two trips, and the teacher deletes
// one — and an edit merges only the fields that changed
// (`trip_update_document` in core/src/mileage.rs), so two devices editing
// different fields of one trip offline both land. What each field is, and what
// MMS asks for, is the core's header; this provider keeps the reads and the
// batches.
//
// **Every action's writes are the core's plan (#554).** A save, an edit of
// one date or of a date and those after it, a delete, "update the existing
// entries" and the year's top-up each come from the core as one ordered list
// — the trip, its expenses (#520, one per date of a series, ids derived from
// the trip's so two devices converge), and the expenses it leaves behind —
// and `commit` writes the list as it is. A list can pass a batch's 500
// writes (a daily series is 365 expenses a year), so it is chunked as the
// expense import is, the trip in the first chunk. `expense` is Business
// Settings' Mileage Log tab as `expenseSettingsFor` reads it, handed in by
// the caller (`useMileageExpenseSettings`), so this provider reads nothing
// but trips.

const MileageContext = createContext(null);
const TRACK_LIMIT = 5000;
// A batch caps at 500 writes; the expense import's chunk, under it.
const CHUNK = 400;

const SENTINELS = () => ({ serverTimestamp: serverTimestamp(), arrayUnion });

export function MileageProvider({ children }) {
  const { user } = useAuth();

  // The mileage window, from whichever read path serves this session (#207
  // U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges` as
  // the rollback; `pendingWrite` is spread by the default row.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.mileageEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const trips = read.docs;

  const value = useMemo(() => {
    // A plan's writes, in order, `CHUNK` to a batch. Answers how many.
    const commit = async (plan) => {
      if (!user || !plan?.length) return 0;
      for (let i = 0; i < plan.length; i += CHUNK) {
        const batch = writeBatch(db);
        for (const w of plan.slice(i, i + CHUNK)) {
          const ref = docRef(user.uid, w.collection, w.id);
          if (w.kind === 'delete') batch.delete(ref);
          else if (w.kind === 'merge') batch.set(ref, w.document, { merge: true });
          else batch.set(ref, w.document);
        }
        await batch.commit();
      }
      return plan.length;
    };

    return {
      trips,
      loading: read.loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,

      // `form` is the drawer's (`blankTripForm`/`tripFormForDate`); a form the
      // refusals refuse writes nothing and answers null, which the drawer never
      // hands over — Save shows the refusals instead.
      addTrip: async (form, expense = null) => {
        if (!user) return null;
        const id = freeId('mil');
        const plan = planAdd(id, form, expense, todayISO(), SENTINELS());
        if (!plan) return null;
        await commit(plan);
        return id;
      },

      // `trip` is the stored document, `date` the row's the drawer opened at,
      // `scope` the answer to "Update Recurring Entries" for a date of a
      // series; `before` is what the drawer opened with and `after` what it
      // holds. An edit that changes nothing writes nothing.
      updateTrip: async (trip, date, scope, before, after, expense = null) => {
        if (!user) return false;
        const plan = planEdit(trip, date, scope, before, after, expense, todayISO(), SENTINELS());
        if (!plan) return false;
        await commit(plan);
        return true;
      },

      // A one-time trip, or one date of a series and perhaps those after it.
      removeTrip: async (trip, date, scope) => {
        if (!user) return;
        await commit(planDelete(trip, date, scope, todayISO(), SENTINELS()));
      },

      // "Yes, update the existing mileage log entries", Starting On `from`:
      // how many expenses it would write, and the writing. Answers how many it
      // wrote.
      countExistingExpenses: (expense, from) => planUpdateExisting(trips, expense, from, todayISO()).length,
      updateExistingExpenses: async (expense, from) => {
        if (!user || !expense) return 0;
        return commit(planUpdateExisting(trips, expense, from, todayISO()));
      },

      // What the studio's series are owed — the year's turn, and a series
      // saved before #554 — given the expense ids it holds. Usually nothing.
      //
      // **Only the expenses the server says are not there (#631).** `presentIds`
      // is this device's read, which can be a previous session's or a cold
      // cache's and miss expenses that exist — and each planned expense is a
      // merge of the priced document, so writing one that exists puts its
      // amount and description back over a teacher's edit. So the plan's
      // writes are checked against the server (and against what this device
      // still owes, ./serverConfirmed.js) and the ones that exist dropped; a
      // legacy entry's delete stays, since that entry is going either way.
      // Offline this rejects and writes nothing; `useMileageTopUp` lets it go
      // and the next visit asks again.
      topUpExpenses: async (presentIds, expense) => {
        if (!user || !expense) return 0;
        const plan = planTopUp(trips, presentIds, expense, todayISO());
        if (!plan.length) return 0;
        const refOf = (w) => docRef(user.uid, w.collection, w.id);
        const absent = await absentAtServer(plan.filter((w) => w.kind !== 'delete').map(refOf));
        return commit(plan.filter((w) => w.kind === 'delete' || absent.has(refOf(w).path)));
      },
    };
  }, [trips, read.loading, read.failed, user]);

  return <MileageContext.Provider value={value}>{children}</MileageContext.Provider>;
}

export function useMileage() {
  const ctx = useContext(MileageContext);
  if (!ctx) throw new Error('useMileage must be used within a MileageProvider');
  return ctx;
}
